import { useMemo } from "react"
import config from "@root/config.js"
import { getMetafield } from "@app/utils/product"

export const useProductComputed = (product: any) => {
  const productInfo = useMemo(() => getMetafield(product, "sanity", "product_info"), [product])
  const brandbankId = useMemo(() => getMetafield(product, "sanity", "brandbank_id"), [product])
  const ingredients = useMemo(() => getMetafield(product, "sanity", "ingredients"), [product])
  const secondaryTitle = useMemo(() => getMetafield(product, "sanity", "secondary_title"), [product])
  const reviews = useMemo(() => {
    const raw = getMetafield(product, "yotpo", "reviews")
    try {
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }, [product])

  const swatch = useMemo(() => {
    const name = product?.tags?.find((tag: string) => tag?.startsWith("swatch:"))?.split(":")?.[1]
    return name ? `https://allkinds-swatches.s3.ap-southeast-2.amazonaws.com/${name}` : null
  }, [product])

  const color = useMemo(() => {
    const color = product?.tags
      ?.find((tag: string) => tag?.startsWith("colour:"))
      ?.split(":")?.[1]
      ?.replaceAll(" ", "")
    return color ? color?.charAt(0)?.toLowerCase() + color?.slice(1) : null
  }, [product])

  const isGiftCard = useMemo(() => product?.productType === config?.store?.giftCardType, [product?.productType])

  // productSize is a string in grams e.g 250g or ml e.g 250ml
  const productSize: string = useMemo(() => product?.tags?.find((tag: string) => tag.startsWith("size:"))?.split(":")?.[1], [product])

  const productSizeText = useMemo(() => {
    const gramsToOz = 0.035274
    const mlToOz = 0.033814

    if (productSize?.toLowerCase().includes("ml")) return `${productSize} / ${(parseInt(productSize) * mlToOz).toFixed(1)} fl oz`
    if (productSize?.toLowerCase()?.includes("g")) return `${productSize} / ${(parseInt(productSize) * gramsToOz).toFixed(1)} oz`
    return ""
  }, [productSize])

  // Coming-soon availability copy from the `available:<text>` tag e.g. `available:Available 24 June`.
  // The value can contain spaces and further colons, so split on the FIRST colon only.
  const availabilityText: string = useMemo(() => {
    const tag = product?.tags?.find((tag: string) => tag?.startsWith("available:"))
    if (!tag) return ""
    const index = tag.indexOf(":")
    return index >= 0 ? tag.slice(index + 1).trim() : ""
  }, [product])

  return { productInfo, brandbankId, ingredients, secondaryTitle, reviews, swatch, color, isGiftCard, productSizeText, availabilityText }
}
